import { ThemeEvents } from '@theme/events';
import { debounce, prefersReducedMotion } from '@theme/utilities';

/**
 * "Recommended for You" carousel inside the cart drawer.
 *
 * Fetches Shopify product recommendations (via the Section Rendering API)
 * for the first product in the cart, excluding products already in the cart
 * (filtered server-side in sections/cart-drawer-recommendations.liquid).
 *
 * The drawer is re-rendered (morphed) on every cart change, which resets the
 * carousel to its server-rendered empty state; a MutationObserver re-injects
 * the cached slides when that happens.
 */
class CartDrawerRecommendations extends HTMLElement {
  /** @type {string | null} */
  #cacheKey = null;

  /** @type {DocumentFragment | null} */
  #cachedSlides = null;

  /** @type {AbortController | null} */
  #activeFetch = null;

  #debouncedUpdate = debounce(() => this.#update(), 200);

  #observer = new MutationObserver(() => this.#restoreFromCache());

  connectedCallback() {
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.addEventListener('click', this.#onClick);
    this.#observer.observe(this, { childList: true, subtree: true });
    this.#update();
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.removeEventListener('click', this.#onClick);
    this.#observer.disconnect();
    this.#activeFetch?.abort();
  }

  #onCartUpdate = () => this.#debouncedUpdate();

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const direction = event.target.closest('[data-recs-next]') ? 1 : event.target.closest('[data-recs-prev]') ? -1 : 0;
    if (!direction) return;

    const track = this.#track;
    const slide = track?.querySelector('[data-recs-slide]');
    if (!track || !slide) return;

    track.scrollBy({
      left: direction * (slide.clientWidth + 12) * (document.dir === 'rtl' ? -1 : 1),
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  };

  get #track() {
    return this.querySelector('[data-recs-track]');
  }

  #restoreFromCache() {
    const track = this.#track;
    if (!track || !this.#cachedSlides) return;
    if (track.querySelector('[data-recs-slide]')) return;

    track.replaceChildren(this.#cachedSlides.cloneNode(true));
    this.hidden = false;
  }

  async #update() {
    try {
      this.#activeFetch?.abort();
      const abortController = new AbortController();
      this.#activeFetch = abortController;

      const cartResponse = await fetch(Theme.routes.cart_url + '.js', { signal: abortController.signal });
      const cart = await cartResponse.json();
      const productIds = [...new Set(cart.items?.map((/** @type {{product_id: number}} */ item) => item.product_id))];

      if (productIds.length === 0) {
        this.#cacheKey = null;
        this.#cachedSlides = null;
        this.hidden = true;
        return;
      }

      const cacheKey = productIds.join(',');
      if (cacheKey === this.#cacheKey) {
        this.#restoreFromCache();
        return;
      }

      const url = `${this.dataset.url}?section_id=cart-drawer-recommendations&product_id=${productIds[0]}&limit=${
        this.dataset.limit || 8
      }&intent=related`;
      const response = await fetch(url, { signal: abortController.signal });
      const text = await response.text();

      const newTrack = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-recs-track]');
      const slides = newTrack ? newTrack.querySelectorAll('[data-recs-slide]') : [];

      if (!slides.length) {
        this.#cacheKey = cacheKey;
        this.#cachedSlides = null;
        this.hidden = true;
        return;
      }

      const fragment = document.createDocumentFragment();
      slides.forEach((slide) => fragment.appendChild(slide));

      this.#cacheKey = cacheKey;
      this.#cachedSlides = /** @type {DocumentFragment} */ (fragment.cloneNode(true));
      this.#track?.replaceChildren(fragment);
      this.hidden = false;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error(error);
    }
  }
}

if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
}
